import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { auth, studentService, courseService, enrollmentService, attendanceService, paymentService, batchService, formatRwf, SECONDARY_TRADES } from '../services';
import { computeAllBalances } from '../utils/paymentSummary';
import { getStudentBatchId, getBatchLabel } from '../utils/studentDisplay';
import { getTradeFromRecord } from '../utils/secondaryAttendance';
import { generateAttendanceReportPdf, buildAttendanceExcelRows } from '../utils/attendanceReportPdf';

const REPORT_TYPES = [
  { value: 'students',    label: 'Students',               icon: '👨‍🎓', accent: '#60A5FA', bg: 'rgba(96,165,250,0.1)' },
  { value: 'attendance',  label: 'Attendance',             icon: '✅', accent: '#8B5CF6', bg: 'rgba(139,92,246,0.1)' },
  { value: 'payments',    label: 'Student Payments',       icon: '💰', accent: '#10B981', bg: 'rgba(16,185,129,0.1)' },
];

const PAYMENT_STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'paid', label: 'Full payment' },
  { value: 'partial', label: 'Partial payment' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'unpaid_attending', label: 'Attending but unpaid' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const buildPreviewRows = (reportType, rows) => {
  if (reportType === 'students') {
    return rows.map((row) => ({
      name: row[0],
      program: row[2],
      batch: row[3],
      gender: row[4],
      phone: row[6],
      email: row[7],
      school: row[8],
      level: row[9],
    }));
  }
  if (reportType === 'attendance') {
    return rows.map((row) => ({
      student: row[0],
      field: row[1],
      level: row[2],
      date: row[3],
      status: row[4],
    }));
  }
  if (reportType === 'payments') {
    return rows.map((row) => ({
      name: row[0],
      email: row[1],
      batch: row[4],
      field: row[5],
      attendance: row[7],
      totalDue: row[8],
      totalPaid: row[9],
      balance: row[10],
      status: row[11],
      lastPayment: row[12],
      method: row[13],
    }));
  }
  return rows.map((row) => row.reduce((acc, cell, i) => ({ ...acc, [`col${i}`]: cell }), {}));
};

const studentMatchesBatch = (student, batchFilter) => {
  if (!batchFilter) return true;
  if (batchFilter === '__none__') return !getStudentBatchId(student);
  return getStudentBatchId(student) === batchFilter;
};

const getPaymentMethodLabel = (p) => {
  if (!p) return '—';
  if (p.method === 'office') return 'Office';
  if (p.method === 'bank_transfer' || p.receiptPath) return 'Bank Transfer';
  if (p.method === 'mobile_money' || p.paymentProof) return 'Mobile Money';
  return '—';
};

const getPaymentStatusLabel = (status) => {
  if (status === 'paid') return 'Full';
  if (status === 'partial') return 'Partial';
  return 'Unpaid';
};

const getGeneratedByFooter = () => {
  const user = auth.getUser();
  const name = user?.username || 'Administrator';
  const date = new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return `Generated by ${name} on ${date}`;
};

const Reports = ({ instructorScope = false }) => {
  const navigate = useNavigate();
  const [reportType, setReportType] = useState(instructorScope ? 'attendance' : 'students');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [courseId, setCourseId] = useState('');
  const [schoolNameFilter, setSchoolNameFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [studentSort, setStudentSort] = useState('level-asc');
  const [attendanceSort, setAttendanceSort] = useState('level-asc');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [rawStudents, setRawStudents] = useState([]);
  const [rawCourses, setRawCourses] = useState([]);
  const [rawEnrollments, setRawEnrollments] = useState([]);
  const [rawAttendance, setRawAttendance] = useState([]);
  const [rawPayments, setRawPayments] = useState([]);
  const [exporting, setExporting] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (!auth.isAuthenticated()) { navigate('/login'); return; }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      if (instructorScope) {
        const userId = auth.getUserId();
        const attendance = await attendanceService.getInstructorAttendance(userId).catch(() => []);
        setRawAttendance(Array.isArray(attendance) ? attendance : []);
        setRawStudents([]);
        setRawCourses([]);
        setRawEnrollments([]);
        setRawPayments([]);
        setBatches([]);
        setCourses([]);
      } else {
        const [students, coursesList, enrollments, attendance, payments, batchesList] = await Promise.all([
          studentService.getAllStudents(),
          courseService.getAllCourses(),
          enrollmentService.getAllEnrollments(),
          attendanceService.getAllAttendance(),
          paymentService.getAll().catch(() => []),
          batchService.getAllBatches().catch(() => []),
        ]);
        setRawStudents(Array.isArray(students) ? students : []);
        setRawCourses(Array.isArray(coursesList) ? coursesList : []);
        setRawEnrollments(Array.isArray(enrollments) ? enrollments : []);
        setRawAttendance(Array.isArray(attendance) ? attendance : []);
        setRawPayments(Array.isArray(payments) ? payments : []);
        setBatches(Array.isArray(batchesList) ? batchesList : []);
        setCourses(Array.isArray(coursesList) ? coursesList : []);
        if (coursesList?.length && !courseId) setCourseId('');
      }
    } catch (err) {
      console.error('Error loading report data:', err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { applyFilters(); }, [reportType, dateFrom, dateTo, courseId, schoolNameFilter, levelFilter, studentSort, attendanceSort, paymentStatusFilter, fieldFilter, batchFilter, rawStudents, rawCourses, rawEnrollments, rawAttendance, rawPayments]);

  useEffect(() => { setCurrentPage(1); }, [reportType, dateFrom, dateTo, courseId, schoolNameFilter, levelFilter, studentSort, attendanceSort, paymentStatusFilter, fieldFilter, batchFilter, pageSize]);

  const previewRows = useMemo(
    () => buildPreviewRows(reportType, filteredData),
    [reportType, filteredData]
  );

  const totalPages = Math.max(1, Math.ceil(previewRows.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const rangeStart = previewRows.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, previewRows.length);
  const paginatedRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return previewRows.slice(start, start + pageSize);
  }, [previewRows, safePage, pageSize]);

  const applyFilters = () => {
    let rows = [], cols = [];
    const filterByDate = (dateStr) => {
      if (!dateStr) return true;
      const d = new Date(dateStr).getTime();
      if (dateFrom && d < new Date(dateFrom).getTime()) return false;
      if (dateTo && d > new Date(dateTo + 'T23:59:59').getTime()) return false;
      return true;
    };

    if (reportType === 'students') {
      let list = rawStudents;
      if (schoolNameFilter) list = list.filter(s => (s.schoolName||'').trim().toLowerCase() === schoolNameFilter.trim().toLowerCase());
      if (levelFilter) list = list.filter(s => (s.level||'').trim().toLowerCase() === levelFilter.trim().toLowerCase());
      if (batchFilter) list = list.filter((s) => studentMatchesBatch(s, batchFilter));
      list = [...list].sort((a, b) => {
        const levelComparison = (a.level || '').localeCompare(b.level || '', undefined, { numeric: true, sensitivity: 'base' });
        if (studentSort === 'level-desc') return -levelComparison;
        if (studentSort === 'name-asc') return (a.fullName || '').localeCompare(b.fullName || '', undefined, { sensitivity: 'base' });
        return levelComparison || (a.fullName || '').localeCompare(b.fullName || '', undefined, { sensitivity: 'base' });
      });
      cols = ['Full Name','Type','Program','Batch','Gender','Date of Birth','Phone','Email','School','Level'];
      rows = list.map(s => [
        s.fullName||'—',
        s.studentType==='ProfessionalStudent'?'Professional':s.studentType==='SecondaryStudent'?'Secondary':(s.studentType||'—'),
        s.course||s.fieldOfStudy||'—',
        getBatchLabel(s),
        s.gender||'—',
        s.dateOfBirth?new Date(s.dateOfBirth).toLocaleDateString():'—',
        s.phone||'—',
        s.email||'—',
        s.schoolName||'—',
        s.level||'—',
      ]);
    } else if (reportType === 'courses') {
      cols = ['Course Name','Instructor'];
      rows = rawCourses.map(c => [c.courseName||'—',c.instructor&&typeof c.instructor==='object'?c.instructor.username:(c.instructor||'—')]);
    } else if (reportType === 'enrollments') {
      const studentById = new Map(rawStudents.map((s) => [String(s._id), s]));
      const resolveStudent = (ref) => {
        if (ref && typeof ref === 'object' && ref.fullName) return ref;
        return studentById.get(String(ref?._id || ref)) || null;
      };
      let list = rawEnrollments;
      if (courseId) list = list.filter(e => (e.course?._id||e.course)===courseId);
      list = list.filter(e => filterByDate(e.enrollmentDate));
      if (batchFilter) list = list.filter((e) => studentMatchesBatch(resolveStudent(e.student), batchFilter));
      cols = ['Student','Course','Enrollment Date'];
      rows = list.map(e => [e.student&&typeof e.student==='object'?e.student.fullName:'—',e.course&&typeof e.course==='object'?e.course.courseName:'—',e.enrollmentDate?new Date(e.enrollmentDate).toLocaleDateString():'—']);
    } else if (reportType === 'attendance') {
      const studentById = new Map(rawStudents.map((s) => [String(s._id), s]));
      const resolveStudent = (ref) => {
        if (ref && typeof ref === 'object' && ref.fullName) return ref;
        return studentById.get(String(ref?._id || ref)) || null;
      };
      const resolveAttendanceField = (r) => {
        const stu = resolveStudent(r.student);
        const fromRecord = getTradeFromRecord(stu ? { ...r, student: stu } : r);
        if (fromRecord) return fromRecord;
        return (stu?.fieldOfStudy || stu?.course || '').trim() || '—';
      };
      let list = rawAttendance;
      list = list.filter(r => filterByDate(r.date));
      if (batchFilter) list = list.filter((r) => studentMatchesBatch(resolveStudent(r.student), batchFilter));
      if (fieldFilter) {
        list = list.filter((r) => resolveAttendanceField(r).toLowerCase() === fieldFilter.toLowerCase());
      }
      const getAttendanceLevel = (r) => (resolveStudent(r.student)?.level || '').trim();
      if (levelFilter) {
        list = list.filter((r) => getAttendanceLevel(r).toLowerCase() === levelFilter.trim().toLowerCase());
      }
      list = [...list].sort((a, b) => {
        const levelComparison = getAttendanceLevel(a).localeCompare(getAttendanceLevel(b), undefined, { numeric: true, sensitivity: 'base' });
        if (attendanceSort === 'level-desc') return -levelComparison;
        if (attendanceSort === 'name-asc') {
          return (resolveStudent(a.student)?.fullName || '').localeCompare(resolveStudent(b.student)?.fullName || '', undefined, { sensitivity: 'base' });
        }
        return levelComparison || (resolveStudent(a.student)?.fullName || '').localeCompare(resolveStudent(b.student)?.fullName || '', undefined, { sensitivity: 'base' });
      });
      cols = ['Student', 'Field', 'Level', 'Date', 'Status'];
      rows = list.map((r) => [
        resolveStudent(r.student)?.fullName || '—',
        resolveAttendanceField(r),
        getAttendanceLevel(r) || '—',
        r.date ? new Date(r.date).toLocaleDateString() : '—',
        r.status || '—',
      ]);
    } else if (reportType === 'payments') {
      let balances = computeAllBalances(rawStudents, rawPayments);
      const attendedStudentIds = new Set(
        rawAttendance
          .filter((record) => (record.status || 'present').toLowerCase() === 'present')
          .map((record) => String(record.student?._id || record.student))
      );
      const attendanceCountByStudentId = rawAttendance.reduce((counts, record) => {
        if ((record.status || 'present').toLowerCase() !== 'present') return counts;
        const studentId = String(record.student?._id || record.student);
        counts.set(studentId, (counts.get(studentId) || 0) + 1);
        return counts;
      }, new Map());
      if (paymentStatusFilter) {
        balances = balances.filter((b) => (
          paymentStatusFilter === 'unpaid_attending'
            ? b.status === 'unpaid' && attendedStudentIds.has(b.studentId)
            : b.status === paymentStatusFilter
        ));
      }
      if (levelFilter) {
        balances = balances.filter((bal) => {
          const student = rawStudents.find((s) => String(s._id) === bal.studentId);
          return (student?.level || '').trim().toLowerCase() === levelFilter.trim().toLowerCase();
        });
      }
      cols = ['Student', 'Email', 'Phone', 'School', 'Batch', 'Field', 'Level', 'Attendance', 'Total Due', 'Paid', 'Balance', 'Status', 'Last Payment', 'Method'];
      rows = balances.map((bal) => {
        const stu = rawStudents.find((s) => String(s._id) === bal.studentId) || {};
        if (batchFilter && !studentMatchesBatch(stu, batchFilter)) return null;
        const studentField = (stu.fieldOfStudy || stu.course || '').trim();
        if (fieldFilter && studentField.toLowerCase() !== fieldFilter.toLowerCase()) return null;
        const studentPayments = rawPayments
          .filter((p) => String(p.student?._id || p.student) === bal.studentId)
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        const lastPay = studentPayments[0];
        if (dateFrom || dateTo) {
          const hasAttendanceInRange = rawAttendance.some((record) => (
            String(record.student?._id || record.student) === bal.studentId
            && (record.status || 'present').toLowerCase() === 'present'
            && filterByDate(record.date)
          ));
          if (paymentStatusFilter === 'unpaid_attending') {
            if (!hasAttendanceInRange) return null;
          } else if (!lastPay || !filterByDate(lastPay.createdAt)) {
            return null;
          }
        }
        return [
          stu.fullName || '—',
          stu.email || '—',
          stu.phone || '—',
          stu.schoolName || '—',
          getBatchLabel(stu),
          studentField || '—',
          stu.level || '—',
          attendanceCountByStudentId.has(bal.studentId)
            ? `Present (${attendanceCountByStudentId.get(bal.studentId)} day${attendanceCountByStudentId.get(bal.studentId) === 1 ? '' : 's'})`
            : 'No attendance recorded',
          formatRwf(bal.totalDue),
          formatRwf(bal.totalPaid),
          formatRwf(bal.balance),
          getPaymentStatusLabel(bal.status),
          lastPay?.createdAt ? new Date(lastPay.createdAt).toLocaleDateString() : '—',
          getPaymentMethodLabel(lastPay),
        ];
      }).filter(Boolean);
    }
    setColumns(cols);
    setFilteredData(rows);
  };

  const getFilterNote = () => {
    const parts = [];
    if (batchFilter) {
      const batchName = batchFilter === '__none__'
        ? 'No batch assigned'
        : (batches.find((b) => b._id === batchFilter)?.name || 'Selected batch');
      parts.push(`Batch: ${batchName}`);
    }
    if (reportType === 'payments') {
      if (paymentStatusFilter) {
        parts.push(PAYMENT_STATUS_FILTERS.find((f) => f.value === paymentStatusFilter)?.label || paymentStatusFilter);
      }
    }
    if ((reportType === 'payments' || reportType === 'attendance') && fieldFilter) {
      parts.push(`Field: ${fieldFilter}`);
    }
    if (reportType === 'payments' && levelFilter) {
      parts.push(`Level: ${levelFilter}`);
    }
    return parts.length ? ` — ${parts.join(' · ')}` : '';
  };

  const buildAttendanceExportMeta = () => {
    const meta = [];
    if (fieldFilter) {
      meta.push({ label: 'Field of study', value: fieldFilter });
    } else if (instructorScope && instructorTrades.length) {
      meta.push({ label: 'Field of study', value: instructorTrades.join(', ') });
    } else if (reportType === 'attendance') {
      meta.push({ label: 'Field of study', value: 'All fields' });
    }
    if (reportType === 'attendance') {
      meta.push({ label: 'Level', value: levelFilter || 'All levels' });
    }
    if (dateFrom || dateTo) {
      meta.push({
        label: 'Date range',
        value: `${dateFrom || 'Start'} — ${dateTo || 'End'}`,
      });
    }
    if (!instructorScope && batchFilter) {
      const batchName = batchFilter === '__none__'
        ? 'No batch assigned'
        : (batches.find((b) => b._id === batchFilter)?.name || 'Selected batch');
      meta.push({ label: 'Batch', value: batchName });
    }
    meta.push({ label: 'Total records', value: String(filteredData.length) });
    return meta;
  };

  const exportPDF = async () => {
    setExporting('pdf');
    try {
      const label = REPORT_TYPES.find(r => r.value === reportType)?.label || reportType;
      const filename = `${instructorScope ? 'instructor-attendance' : `report-${reportType}`}-${new Date().toISOString().slice(0, 10)}.pdf`;

      if (reportType === 'attendance' || instructorScope) {
        await generateAttendanceReportPdf({
          title: instructorScope ? 'Attendance Report' : `Report: ${label}`,
          meta: buildAttendanceExportMeta(),
          head: [columns],
          body: filteredData.map((row) => row.map((cell) => String(cell ?? '—'))),
          generatedBy: getGeneratedByFooter(),
          filename,
          landscape: true,
        });
        return;
      }

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        compress: true,
        putOnlyUsedFonts: true,
        precision: 2,
      });
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(16); doc.text(`Report: ${label}${getFilterNote()}`, 14, 16);
      doc.setFontSize(10); doc.text('Fabritech Training Center', 14, 22);
      autoTable(doc, {
        startY: 28, head: [columns], body: filteredData, theme: 'grid',
        styles: { fontSize: 9 }, headStyles: { fillColor: [55, 65, 81] },
        didDrawPage: (data) => {
          doc.setFontSize(8); doc.setTextColor(128,128,128);
          doc.text(getGeneratedByFooter(), data.settings.margin.left, pageHeight - 10);
          doc.text(`Page ${doc.internal.getNumberOfPages()}`, doc.internal.pageSize.getWidth() - 20, pageHeight - 10);
        },
      });
      doc.save(filename);
    } finally { setExporting(''); }
  };

  const exportExcel = () => {
    setExporting('excel');
    try {
      const label = REPORT_TYPES.find(r => r.value === reportType)?.label || reportType;
      const filename = `${instructorScope ? 'instructor-attendance' : `report-${reportType}`}-${new Date().toISOString().slice(0, 10)}.xlsx`;

      if (reportType === 'attendance' || instructorScope) {
        const wsData = buildAttendanceExcelRows({
          title: instructorScope ? 'Attendance Report' : `Report: ${label}`,
          meta: buildAttendanceExportMeta(),
          head: columns,
          body: filteredData,
          generatedBy: getGeneratedByFooter(),
        });
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = columns.map((_, i) => ({
          wch: Math.min(28, Math.max(12, (filteredData[0] && filteredData[0][i]?.toString().length) || 10)),
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Report');
        XLSX.writeFile(wb, filename, { compression: true });
        return;
      }

      const wsData = [['Fabritech Training Center'], [`Report: ${label}${getFilterNote()}`], [], columns, ...filteredData, [], [getGeneratedByFooter()]];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = columns.map((_,i) => ({ wch: Math.min(28, Math.max(12, (filteredData[0]&&filteredData[0][i]?.toString().length)||10)) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Report');
      XLSX.writeFile(wb, filename, { compression: true });
    } finally { setExporting(''); }
  };

  const activeType = REPORT_TYPES.find(r => r.value === reportType);
  const schoolNames = [...new Set(rawStudents.map(s => (s.schoolName||'').trim()).filter(Boolean))].sort();
  const levels = [...new Set([
    ...rawStudents.map((s) => (s.level || '').trim()),
    ...rawAttendance.map((r) => (r.student && typeof r.student === 'object' ? (r.student.level || '').trim() : '')),
  ].filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  const needsCourse = false;
  const needsDate = true;
  const needsSchool = reportType === 'students';
  const needsLevel = reportType === 'students' || reportType === 'attendance' || reportType === 'payments';
  const needsStudentSort = reportType === 'students';
  const needsAttendanceSort = reportType === 'attendance';
  const needsPaymentStatus = reportType === 'payments';
  const needsField = reportType === 'payments' || reportType === 'attendance';
  const needsBatch = !instructorScope;
  const sortedBatches = useMemo(
    () => [...batches].sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0)),
    [batches]
  );
  const instructorTrades = useMemo(() => {
    const fromUser = auth.getUser()?.assignedTrades;
    return Array.isArray(fromUser) ? fromUser.map((t) => String(t).trim()).filter(Boolean) : [];
  }, []);
  const fieldOptions = [...new Set([
    ...(instructorScope ? instructorTrades : SECONDARY_TRADES),
    ...rawStudents.map((s) => (s.fieldOfStudy || s.course || '').trim()).filter(Boolean),
    ...rawAttendance.map((r) => getTradeFromRecord(r)).filter(Boolean),
  ])].sort();
  const paymentReportCount = computeAllBalances(rawStudents, rawPayments).length;

  const statusCell = (val) => {
    if (typeof val !== 'string') return val;
    const v = val.toLowerCase();
    if (v === 'present') return <span style={{ display:'inline-flex',alignItems:'center',gap:'4px',padding:'3px 9px',borderRadius:'20px',background:'rgba(52,211,153,0.12)',border:'1px solid rgba(52,211,153,0.25)',color:'#6EE7B7',fontSize:'11px',fontWeight:700 }}>✓ Present</span>;
    if (v === 'absent')  return <span style={{ display:'inline-flex',alignItems:'center',gap:'4px',padding:'3px 9px',borderRadius:'20px',background:'rgba(248,113,113,0.12)',border:'1px solid rgba(248,113,113,0.25)',color:'#FCA5A5',fontSize:'11px',fontWeight:700 }}>✕ Absent</span>;
    if (v === 'bank transfer') return <span style={{ display:'inline-flex',alignItems:'center',gap:'5px',padding:'3px 9px',borderRadius:'20px',background:'#eff6ff',border:'1px solid #93c5fd',color:'#1d4ed8',fontSize:'11px',fontWeight:700 }}>🏦 Bank</span>;
    if (v === 'mobile money') return <span style={{ display:'inline-flex',alignItems:'center',gap:'5px',padding:'3px 9px',borderRadius:'20px',background:'#fffbeb',border:'1px solid #fcd34d',color:'#b45309',fontSize:'11px',fontWeight:700 }}>📱 MoMo</span>;
    if (v === 'office') return <span style={{ display:'inline-flex',alignItems:'center',gap:'5px',padding:'3px 9px',borderRadius:'20px',background:'#ecfdf5',border:'1px solid #6ee7b7',color:'#047857',fontSize:'11px',fontWeight:700 }}>🏫 Office</span>;
    if (v === 'full') return <span style={{ display:'inline-flex',alignItems:'center',gap:'4px',padding:'3px 9px',borderRadius:'20px',background:'#ecfdf5',border:'1px solid #6ee7b7',color:'#047857',fontSize:'11px',fontWeight:700 }}>✓ Full</span>;
    if (v === 'partial') return <span style={{ display:'inline-flex',alignItems:'center',gap:'4px',padding:'3px 9px',borderRadius:'20px',background:'#fffbeb',border:'1px solid #fcd34d',color:'#b45309',fontSize:'11px',fontWeight:700 }}>◐ Partial</span>;
    if (v === 'unpaid') return <span style={{ display:'inline-flex',alignItems:'center',gap:'4px',padding:'3px 9px',borderRadius:'20px',background:'#fef2f2',border:'1px solid #fca5a5',color:'#b91c1c',fontSize:'11px',fontWeight:700 }}>✕ Unpaid</span>;
    return val;
  };

  const renderGenderBadge = (gender) => {
    if (!gender || gender === '—') return null;
    return <span className="rp-gender-chip">{gender}</span>;
  };

  const renderFeesStrip = (due, paid, balance) => {
    const isClear = !balance || balance === '0' || balance === '—';
    return (
      <div className="rp-fees-strip">
        <div className="rp-fee-item">
          <span className="rp-fee-lbl">Due</span>
          <span className="rp-fee-val muted">{due}</span>
        </div>
        <div className="rp-fee-item">
          <span className="rp-fee-lbl">Paid</span>
          <span className="rp-fee-val">{paid}</span>
        </div>
        <div className="rp-fee-item">
          <span className="rp-fee-lbl">Balance</span>
          <span className={`rp-fee-val${isClear ? ' clear' : ' due'}`}>{balance}</span>
        </div>
      </div>
    );
  };

  const renderStudentPreviewRow = (row, i) => (
    <div key={i} className="rp-data-row rp-data-row--students" style={{ animationDelay: `${i * 25}ms` }}>
      <div className="rp-student-cell">
        <div className="rp-student-name" title={row.name}>{row.name}</div>
        {renderGenderBadge(row.gender)}
      </div>
      <div className="rp-cell-text" title={row.program}>{row.program}</div>
      <div className="rp-cell-text" title={row.batch}>{row.batch}</div>
      <div className="rp-contact-cell">
        {row.phone && row.phone !== '—' && <span>{row.phone}</span>}
        {row.email && row.email !== '—' && <span className="rp-contact-email" title={row.email}>{row.email}</span>}
        {(!row.phone || row.phone === '—') && (!row.email || row.email === '—') && <span className="rp-cell-muted">—</span>}
      </div>
      <div className="rp-cell-text" title={row.school}>{row.school}</div>
      <div className="rp-cell-text rp-cell-compact">{row.level}</div>
    </div>
  );

  const renderAttendancePreviewRow = (row, i) => (
    <div key={i} className="rp-data-row rp-data-row--attendance" style={{ animationDelay: `${i * 25}ms` }}>
      <div className="rp-student-name" title={row.student}>{row.student}</div>
      <div className="rp-cell-text" title={row.field}>{row.field}</div>
      <div className="rp-cell-text">{row.level}</div>
      <div className="rp-cell-date">{row.date}</div>
      <div>{statusCell(row.status)}</div>
    </div>
  );

  const renderPaymentPreviewRow = (row, i) => (
    <div key={i} className={`rp-data-row rp-data-row--payments status-${String(row.status || '').toLowerCase()}`} style={{ animationDelay: `${i * 25}ms` }}>
      <div className="rp-pay-student">
        <div className="rp-student-name" title={row.name}>{row.name}</div>
        <div className="rp-pay-student-meta">
          {row.field && row.field !== '—' && <span title={row.field}>{row.field}</span>}
          {row.batch && row.batch !== '—' && <span className="rp-batch-chip">{row.batch}</span>}
        </div>
        {row.email && row.email !== '—' && (
          <div className="rp-contact-email" title={row.email}>{row.email}</div>
        )}
      </div>
      {renderFeesStrip(row.totalDue, row.totalPaid, row.balance)}
      <div>{statusCell(row.status)}</div>
      <div className="rp-pay-last">
        <span className="rp-pay-last-date">{row.lastPayment}</span>
        {row.method && row.method !== '—' && <span className="rp-pay-last-method">{statusCell(row.method)}</span>}
      </div>
    </div>
  );

  const renderPreviewListHead = () => {
    if (reportType === 'students') {
      return (
        <div className="rp-data-head rp-data-head--students">
          <span>Student</span><span>Program</span><span>Batch</span><span>Contact</span><span>School</span><span>Level</span>
        </div>
      );
    }
    if (reportType === 'attendance') {
      return (
        <div className="rp-data-head rp-data-head--attendance">
          <span>Student</span><span>Field</span><span>Level</span><span>Date</span><span>Status</span>
        </div>
      );
    }
    if (reportType === 'payments') {
      return (
        <div className="rp-data-head rp-data-head--payments">
          <span>Student</span><span>Amounts</span><span>Status</span><span>Last payment</span>
        </div>
      );
    }
    return (
      <div className="rp-data-head rp-data-head--generic" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
        {columns.map((col) => <span key={col}>{col}</span>)}
      </div>
    );
  };

  const renderPreviewListRow = (row, i) => {
    if (reportType === 'students') return renderStudentPreviewRow(row, i);
    if (reportType === 'attendance') return renderAttendancePreviewRow(row, i);
    if (reportType === 'payments') return renderPaymentPreviewRow(row, i);
    return (
      <div key={i} className="rp-data-row rp-data-row--generic" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`, animationDelay: `${i * 25}ms` }}>
        {columns.map((col, j) => (
          <div key={col} className="rp-cell-text">{statusCell(row[`col${j}`])}</div>
        ))}
      </div>
    );
  };

  const renderMobileCard = (row, i) => {
    if (reportType === 'students') {
      return (
        <div key={i} className="rp-card" style={{ animationDelay: `${i * 30}ms` }}>
          <div className="rp-card-title-row">
            <span className="rp-card-title">{row.name}</span>
            {renderGenderBadge(row.gender)}
          </div>
          <div className="rp-card-row"><span className="rp-card-label">Program</span><span className="rp-card-val">{row.program}</span></div>
          <div className="rp-card-row"><span className="rp-card-label">Batch</span><span className="rp-card-val">{row.batch}</span></div>
          <div className="rp-card-row"><span className="rp-card-label">Contact</span><span className="rp-card-val">{row.phone}<br />{row.email}</span></div>
          <div className="rp-card-row"><span className="rp-card-label">School</span><span className="rp-card-val">{row.school}</span></div>
          <div className="rp-card-row"><span className="rp-card-label">Level</span><span className="rp-card-val">{row.level}</span></div>
        </div>
      );
    }
    if (reportType === 'attendance') {
      return (
        <div key={i} className="rp-card" style={{ animationDelay: `${i * 30}ms` }}>
          <div className="rp-card-title-row"><span className="rp-card-title">{row.student}</span>{statusCell(row.status)}</div>
          <div className="rp-card-row"><span className="rp-card-label">Field</span><span className="rp-card-val">{row.field}</span></div>
          <div className="rp-card-row"><span className="rp-card-label">Level</span><span className="rp-card-val">{row.level}</span></div>
          <div className="rp-card-row"><span className="rp-card-label">Date</span><span className="rp-card-val">{row.date}</span></div>
        </div>
      );
    }
    if (reportType === 'payments') {
      return (
        <div key={i} className="rp-card" style={{ animationDelay: `${i * 30}ms` }}>
          <div className="rp-card-title-row">
            <span className="rp-card-title">{row.name}</span>
            {statusCell(row.status)}
          </div>
          <div className="rp-card-fees">{renderFeesStrip(row.totalDue, row.totalPaid, row.balance)}</div>
          <div className="rp-card-row"><span className="rp-card-label">Field</span><span className="rp-card-val">{row.field}</span></div>
          <div className="rp-card-row"><span className="rp-card-label">Batch</span><span className="rp-card-val">{row.batch}</span></div>
          <div className="rp-card-row"><span className="rp-card-label">Last payment</span><span className="rp-card-val">{row.lastPayment}</span></div>
          <div className="rp-card-row"><span className="rp-card-label">Method</span><span className="rp-card-val">{statusCell(row.method)}</span></div>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <style>{`
        
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .rp-root {
          
          --primary-blue: #1e40af;
          --secondary-blue: #3b82f6;
          --accent-orange: #ea580c;
          --text-dark: #111827;
          --text-medium: #374151;
          --text-light: #6b7280;
          --border-light: #e5e7eb;
          --border-medium: #d1d5db;
          --background-gray: #f8fafc;
          --background-white: #ffffff;
          --surface2: #f9fafb;
          --muted: #6b7280;
          --muted2: #374151;
          --border: #e5e7eb;
          --success-green: #059669;
          --error-red: #dc2626;
          --warning-yellow: #d97706;
          --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1);
          --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1);
          
          background: var(--background-gray);
          color: var(--text-dark);
          font-family: system-ui, -apple-system, 'Inter', sans-serif;
          min-height: 100vh;
          padding: 28px 24px;
        }

        /* ── PAGE HEADER ── */
        .rp-page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 28px; flex-wrap: wrap; }
        .rp-eyebrow {
          display: inline-flex; align-items: center; gap: 6px;
          background: var(--border-light); border: 1px solid var(--border-light);
          border-radius: 12px; padding: 4px 12px;
          font-size: 11px; font-weight: 700; color: var(--muted2);
          letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 10px;
        }
        .rp-page-title { font-family: inherit; font-size: 28px; font-weight: 800; letter-spacing: -0.5px; line-height: 1.1; }
        .rp-page-title span {
          background: var(--primary-blue);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .rp-page-sub { font-size: 13px; color: var(--text-light); margin-top: 6px; }

        /* ── REPORT TYPE PICKER ── */
        .rp-type-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 24px; }
        .rp-type-card {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 14px 12px;
          cursor: pointer; transition: all 0.2s;
          display: flex; flex-direction: column; align-items: center; gap: 8px;
          text-align: center; position: relative; overflow: hidden;
        }
        .rp-type-card::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
          background: var(--c, transparent); opacity: 0; transition: opacity 0.2s;
        }
        .rp-type-card:hover { border-color: var(--border-light); transform: translateY(-2px); }
        .rp-type-card.active {
          border-color: var(--c); background: var(--bg-c);
          box-shadow: var(--shadow-md);
        }
        .rp-type-card.active::before { opacity: 1; }
        .rp-type-icon { font-size: 24px; }
        .rp-type-label { font-size: 12px; font-weight: 700; color: var(--muted2); transition: color 0.2s; }
        .rp-type-card.active .rp-type-label { color: var(--c); }
        .rp-type-count { font-family: inherit; font-size: 18px; font-weight: 800; color: var(--text-dark); line-height: 1; }

        /* ── FILTER PANEL ── */
        .rp-filter-panel {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 12px; padding: 24px 28px; margin-bottom: 24px;
          animation: rpSlideIn 0.25s ease;
        }
        @keyframes rpSlideIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        .rp-filter-head {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 20px; flex-wrap: wrap; gap: 12px;
        }
        .rp-filter-title {
          font-family: inherit; font-size: 15px; font-weight: 700;
          display: flex; align-items: center; gap: 8px;
        }
        .rp-filter-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--c); box-shadow: 0 0 8px var(--c); }

        .rp-filter-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; margin-bottom: 20px; }
        .rp-field { display: flex; flex-direction: column; gap: 7px; }
        .rp-label { font-size: 11px; font-weight: 700; color: var(--muted2); text-transform: uppercase; letter-spacing: 0.6px; }
        .rp-select, .rp-date {
          background: var(--surface); border: 1px solid var(--border-medium);
          border-radius: 8px; padding: 11px 14px;
          font-size: 13px; color: var(--text); font-family: inherit;
          outline: none; width: 100%; transition: border-color 0.2s, box-shadow 0.2s;
        }
        .rp-select:focus, .rp-date:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }
        .rp-select {
          appearance: none; cursor: pointer;
          background-color: #ffffff;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 12px center;
          padding-right: 32px;
        }
        .rp-select option { background: var(--surface); color: var(--text); }
        .rp-date::-webkit-calendar-picker-indicator { filter: invert(0.5); cursor: pointer; }

        /* Clear date button */
        .rp-date-wrap { position: relative; display: flex; align-items: center; }
        .rp-date-wrap .rp-date { flex: 1; }
        .rp-date-clear { position: absolute; right: 10px; background: none; border: none; color: var(--text-light); cursor: pointer; font-size: 14px; padding: 2px; transition: color 0.2s; }
        .rp-date-clear:hover { color: var(--text-dark); }

        /* Export buttons */
        .rp-export-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .rp-export-label { font-size: 12px; color: var(--text-light); font-weight: 600; }
        .rp-btn-pdf {
          display: flex; align-items: center; gap: 7px; padding: 11px 20px;
          background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.25);
          border-radius: 6px; color: #FCA5A5;
          font-size: 13px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: all 0.2s; white-space: nowrap;
        }
        .rp-btn-pdf:hover:not(:disabled) { background: rgba(248,113,113,0.18); transform: translateY(-1px); }
        .rp-btn-excel {
          display: flex; align-items: center; gap: 7px; padding: 11px 20px;
          background: rgba(52,211,153,0.1); border: 1px solid rgba(52,211,153,0.25);
          border-radius: 6px; color: #6EE7B7;
          font-size: 13px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: all 0.2s; white-space: nowrap;
        }
        .rp-btn-excel:hover:not(:disabled) { background: rgba(52,211,153,0.18); transform: translateY(-1px); }
        .rp-btn-pdf:disabled, .rp-btn-excel:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

        /* ── PREVIEW SECTION ── */
        .rp-preview-head {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 16px; flex-wrap: wrap; gap: 12px;
        }
        .rp-preview-title {
          font-family: inherit; font-size: 17px; font-weight: 700;
          display: flex; align-items: center; gap: 8px;
        }
        .rp-preview-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--c); box-shadow: 0 0 8px var(--c); }
        .rp-count-badge {
          border-radius: 12px; padding: 2px 10px; font-size: 12px; font-weight: 700;
          background: var(--bg-c); border: 1px solid var(--c); color: var(--c);
        }
        .rp-footer-note {
          font-size: 11px; color: var(--text-light); display: flex; align-items: center; gap: 6px;
          padding: 8px 14px;
          background: var(--border-light); border: 1px solid var(--border-light);
          border-radius: 6px; font-family: monospace;
        }

        /* ── DATA LIST (no horizontal scroll) ── */
        .rp-table-card {
          background: var(--background-white);
          border: 1px solid var(--border-light);
          border-radius: 14px;
          overflow: hidden;
          box-shadow: var(--shadow-sm);
        }
        .rp-data-list {
          display: flex;
          flex-direction: column;
        }
        .rp-data-head,
        .rp-data-row {
          display: grid;
          align-items: center;
          gap: 12px 16px;
          padding: 12px 20px;
        }
        .rp-data-head {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: var(--text-light);
          background: var(--surface2);
          border-bottom: 1px solid var(--border);
        }
        .rp-data-head--students,
        .rp-data-row--students {
          grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr) 0.75fr minmax(0, 1fr) minmax(0, 0.85fr) 0.55fr;
        }
        .rp-data-head--attendance,
        .rp-data-row--attendance {
          grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) 80px 100px 110px;
        }
        .rp-data-head--payments,
        .rp-data-row--payments {
          grid-template-columns: minmax(0, 1.15fr) minmax(200px, auto) 96px minmax(84px, 0.75fr);
        }
        .rp-data-row {
          font-size: 13px;
          color: var(--text-dark);
          border-bottom: 1px solid var(--border-light);
          border-left: 3px solid transparent;
          transition: background 0.15s ease;
          animation: rpFadeUp 0.3s ease both;
        }
        .rp-data-row:last-child { border-bottom: none; }
        .rp-data-row:hover { background: rgba(59, 130, 246, 0.04); }
        .rp-data-row--payments.status-unpaid { border-left-color: #ef4444; }
        .rp-data-row--payments.status-partial { border-left-color: #f59e0b; }
        .rp-data-row--payments.status-full { border-left-color: #10b981; }

        .rp-student-cell {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          flex-wrap: wrap;
        }
        .rp-student-name {
          font-size: 14px;
          font-weight: 700;
          color: var(--text-dark);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
        }
        .rp-gender-chip {
          display: inline-flex;
          padding: 2px 7px;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 700;
          color: var(--text-light);
          background: var(--surface2);
          border: 1px solid var(--border-light);
          flex-shrink: 0;
        }
        .rp-cell-text {
          font-size: 13px;
          color: var(--text-medium);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
        }
        .rp-cell-compact { text-align: left; }
        .rp-cell-muted { color: var(--text-light); font-size: 12px; }
        .rp-cell-date {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-dark);
          font-variant-numeric: tabular-nums;
        }
        .rp-contact-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }
        .rp-contact-cell span,
        .rp-contact-email {
          font-size: 12px;
          color: var(--text-medium);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .rp-contact-email { color: var(--text-light); }

        .rp-fees-strip {
          display: flex;
          align-items: center;
          gap: 20px;
          flex-shrink: 0;
        }
        .rp-fee-item {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
          min-width: 68px;
        }
        .rp-fee-lbl {
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--text-light);
        }
        .rp-fee-val {
          font-size: 13px;
          font-weight: 700;
          font-variant-numeric: tabular-nums;
          color: var(--text-dark);
        }
        .rp-fee-val.muted { color: var(--text-medium); font-weight: 600; }
        .rp-fee-val.due { color: #d97706; font-size: 14px; }
        .rp-fee-val.clear { color: var(--success-green); }

        .rp-pay-student { min-width: 0; }
        .rp-pay-student-meta {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          margin-top: 3px;
          font-size: 11px;
          color: var(--text-light);
        }
        .rp-pay-student-meta span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
        }
        .rp-batch-chip {
          display: inline-flex;
          padding: 2px 7px;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 600;
          color: #1d4ed8;
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          flex-shrink: 0;
        }
        .rp-pay-last {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
          min-width: 0;
        }
        .rp-pay-last-date {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-dark);
          font-variant-numeric: tabular-nums;
        }
        .rp-pay-last-method { transform: scale(0.95); transform-origin: right center; }

        .rp-pagination {
          display: flex; align-items: center; justify-content: space-between; gap: 16px;
          flex-wrap: wrap; padding: 14px 20px;
          border-top: 1px solid var(--border-light); background: var(--bg);
        }
        .rp-pagination-info { font-size: 13px; color: var(--muted); }
        .rp-pagination-info strong { color: var(--text-dark); font-weight: 700; }
        .rp-pagination-controls {
          display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
        }
        .rp-pagination-size {
          padding: 8px 12px; border-radius: 8px; border: 1px solid var(--border-light);
          background: var(--surface); font-size: 13px; font-family: inherit; color: var(--text-dark);
          cursor: pointer; margin-right: 4px;
        }
        .rp-pagination-btn {
          padding: 8px 14px; border-radius: 8px; border: 1px solid var(--border-light);
          background: var(--surface); font-size: 13px; font-weight: 600; font-family: inherit;
          color: var(--text-dark); cursor: pointer; transition: all 0.15s;
        }
        .rp-pagination-btn:hover:not(:disabled) {
          border-color: var(--primary-blue, #3b82f6); color: var(--primary-blue, #3b82f6); background: var(--link-hover-bg);
        }
        .rp-pagination-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .rp-pagination-page {
          font-size: 13px; font-weight: 600; color: var(--text-dark);
          padding: 0 8px; min-width: 100px; text-align: center;
        }

        /* ── MOBILE CARDS ── */
        .rp-cards { display: none; flex-direction: column; gap: 10px; padding: 16px; }
        .rp-card {
          background: var(--surface2); border: 1px solid var(--border-light);
          border-radius: 10px; padding: 14px 16px;
          animation: rpFadeUp 0.3s ease both; transition: border-color 0.2s;
          border-left: 3px solid var(--c);
        }
        .rp-card:hover { border-color: var(--border-medium); }
        .rp-card-title-row {
          display: flex; align-items: center; justify-content: space-between;
          gap: 10px; margin-bottom: 12px; padding-bottom: 10px;
          border-bottom: 1px solid var(--border-light);
        }
        .rp-card-title {
          font-size: 14px; font-weight: 700; color: var(--text-dark);
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .rp-card-fees {
          margin-bottom: 12px;
          padding: 10px 12px;
          background: var(--background-white);
          border: 1px solid var(--border-light);
          border-radius: 8px;
        }
        .rp-card-fees .rp-fees-strip { justify-content: space-between; width: 100%; }
        .rp-card-fees .rp-fee-item { align-items: center; min-width: 0; }
        @keyframes rpFadeUp { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        .rp-card-row {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 12px; padding: 6px 0; border-bottom: 1px solid var(--border-light);
        }
        .rp-card-row:last-child { border-bottom: none; }
        .rp-card-label { font-size: 10px; font-weight: 700; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px; flex-shrink: 0; padding-top: 2px; }
        .rp-card-val { font-size: 12px; color: var(--text-dark); text-align: right; }

        /* ── EMPTY / LOADING ── */
        .rp-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 64px 24px; gap: 12px;
        }
        .rp-empty-icon { font-size: 48px; opacity: 0.25; }
        .rp-empty-title { font-family: inherit; font-size: 17px; font-weight: 700; color: var(--text-light); }
        .rp-empty-sub { font-size: 13px; color: var(--text-light); text-align: center; }

        .rp-loading { display: flex; align-items: center; justify-content: center; padding: 64px; gap: 8px; }
        .rp-dot { width: 8px; height: 8px; border-radius: 50%; animation: rpBounce 1.2s ease-in-out infinite; }
        .rp-dot:nth-child(1) { background: #60A5FA; }
        .rp-dot:nth-child(2) { background: #F472B6; animation-delay: 0.2s; }
        .rp-dot:nth-child(3) { background: #34D399; animation-delay: 0.4s; }
        @keyframes rpBounce { 0%,80%,100% { transform:scale(0.6); opacity:0.4; } 40% { transform:scale(1); opacity:1; } }

        /* ── RESPONSIVE ── */
        @media (max-width: 1024px) { .rp-type-grid { grid-template-columns: repeat(3,1fr); } }
        @media (max-width: 768px) {
          .rp-root { padding: 16px; }
          .rp-page-title { font-size: 22px; }
          .rp-type-grid { grid-template-columns: repeat(3,1fr); }
          .rp-filter-panel { padding: 18px 16px; }
          .rp-data-list { display: none; }
          .rp-cards { display: flex; }
          .rp-pagination { flex-direction: column; align-items: stretch; gap: 12px; }
          .rp-pagination-controls { justify-content: center; }
        }
        @media (max-width: 480px) {
          .rp-type-grid { grid-template-columns: repeat(2,1fr); }
          .rp-export-row { flex-direction: column; }
          .rp-btn-pdf, .rp-btn-excel { width: 100%; justify-content: center; }
        }
      `}</style>

      <div className="rp-root">
        {/* Page Header */}
        <div className="rp-page-header">
          <div>
            <div className="rp-eyebrow">{instructorScope ? '✦ Instructor Portal' : '✦ Admin Panel'}</div>
            <h1 className="rp-page-title">
              {instructorScope ? <>Attendance <span>Report</span></> : <>Reports & <span>Analytics</span></>}
            </h1>
            <p className="rp-page-sub">
              {instructorScope
                ? 'Filter and export attendance for all students you have marked in the current cohort'
                : 'Generate, filter and export data reports for any section'}
            </p>
          </div>
        </div>

        {!instructorScope && (
          <div className="rp-type-grid">
            {REPORT_TYPES.map(rt => {
              const count = rt.value === 'students' ? rawStudents.length
                : rt.value === 'attendance' ? rawAttendance.length
                : paymentReportCount;
              return (
                <div
                  key={rt.value}
                  className={`rp-type-card${reportType === rt.value ? ' active' : ''}`}
                  style={{ '--c': rt.accent, '--bg-c': rt.bg }}
                  onClick={() => setReportType(rt.value)}
                >
                  <div className="rp-type-icon">{rt.icon}</div>
                  <div className="rp-type-count">{loading ? '—' : count}</div>
                  <div className="rp-type-label">{rt.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filter + Export Panel */}
        <div className="rp-filter-panel" style={{ '--c': activeType?.accent }}>
          <div className="rp-filter-head">
            <div className="rp-filter-title">
              <span className="rp-filter-dot" />
              Filters
              <span style={{ fontSize: '14px' }}>{activeType?.icon}</span>
              <span style={{ fontSize: '13px', color: 'var(--muted2)' }}>{activeType?.label}</span>
            </div>
            {(dateFrom || dateTo || courseId || schoolNameFilter || levelFilter || studentSort !== 'level-asc' || attendanceSort !== 'level-asc' || paymentStatusFilter || fieldFilter || batchFilter) && (
              <button
                style={{ background:'none',border:'none',color:'var(--muted)',fontSize:'12px',cursor:'pointer',fontFamily:'var(--font-body)',fontWeight:600,display:'flex',alignItems:'center',gap:'5px' }}
                onClick={() => { setDateFrom(''); setDateTo(''); setCourseId(''); setSchoolNameFilter(''); setLevelFilter(''); setStudentSort('level-asc'); setAttendanceSort('level-asc'); setPaymentStatusFilter(''); setFieldFilter(''); setBatchFilter(''); }}
              >
                ✕ Clear filters
              </button>
            )}
          </div>

          <div className="rp-filter-grid">
            {needsDate && (
              <>
                <div className="rp-field">
                  <div className="rp-label">Date From</div>
                  <div className="rp-date-wrap">
                    <input className="rp-date" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                    {dateFrom && <button className="rp-date-clear" onClick={() => setDateFrom('')}>✕</button>}
                  </div>
                </div>
                <div className="rp-field">
                  <div className="rp-label">Date To</div>
                  <div className="rp-date-wrap">
                    <input className="rp-date" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                    {dateTo && <button className="rp-date-clear" onClick={() => setDateTo('')}>✕</button>}
                  </div>
                </div>
              </>
            )}
            {needsBatch && (
              <div className="rp-field">
                <div className="rp-label">Batch</div>
                <select
                  className="rp-select"
                  value={batchFilter}
                  onChange={(e) => setBatchFilter(e.target.value)}
                >
                  <option value="">All batches</option>
                  <option value="__none__">No batch assigned</option>
                  {sortedBatches.map((batch) => (
                    <option key={batch._id} value={batch._id}>
                      {batch.name} ({batch.track === 'professional' ? 'Professional' : 'Secondary'}
                      {batch.status === 'running' ? ' · Running' : ''})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {needsSchool && (
              <div className="rp-field">
                <div className="rp-label">School</div>
                <select className="rp-select" value={schoolNameFilter} onChange={e => setSchoolNameFilter(e.target.value)}>
                  <option value="">All schools</option>
                  {schoolNames.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>
            )}
            {needsLevel && (
              <div className="rp-field">
                <div className="rp-label">Level</div>
                <select className="rp-select" value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
                  <option value="">All levels</option>
                  {levels.map(lvl => <option key={lvl} value={lvl}>{lvl}</option>)}
                </select>
              </div>
            )}
            {needsStudentSort && (
              <div className="rp-field">
                <div className="rp-label">Sort students by</div>
                <select className="rp-select" value={studentSort} onChange={e => setStudentSort(e.target.value)}>
                  <option value="level-asc">Level (low to high)</option>
                  <option value="level-desc">Level (high to low)</option>
                  <option value="name-asc">Name (A–Z)</option>
                </select>
              </div>
            )}
            {needsAttendanceSort && (
              <div className="rp-field">
                <div className="rp-label">Sort attendance by</div>
                <select className="rp-select" value={attendanceSort} onChange={e => setAttendanceSort(e.target.value)}>
                  <option value="level-asc">Level (low to high)</option>
                  <option value="level-desc">Level (high to low)</option>
                  <option value="name-asc">Student name (A-Z)</option>
                </select>
              </div>
            )}
            {needsPaymentStatus && (
              <div className="rp-field">
                <div className="rp-label">Payment status</div>
                <select
                  className="rp-select"
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                >
                  {PAYMENT_STATUS_FILTERS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
            {needsField && (
              <div className="rp-field">
                <div className="rp-label">Field of study</div>
                <select
                  className="rp-select"
                  value={fieldFilter}
                  onChange={(e) => setFieldFilter(e.target.value)}
                >
                  <option value="">All fields</option>
                  {fieldOptions.map((field) => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
              </div>
            )}
            {needsCourse && (
              <div className="rp-field">
                <div className="rp-label">Course</div>
                <select className="rp-select" value={courseId} onChange={e => setCourseId(e.target.value)}>
                  <option value="">All courses</option>
                  {courses.map(c => <option key={c._id} value={c._id}>{c.courseName}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="rp-export-row">
            <span className="rp-export-label">Export as:</span>
            <button className="rp-btn-pdf" onClick={exportPDF} disabled={loading || filteredData.length === 0 || exporting === 'pdf'}>
              {exporting === 'pdf' ? '⟳ Generating…' : '📄 Export PDF'}
            </button>
            <button className="rp-btn-excel" onClick={exportExcel} disabled={loading || filteredData.length === 0 || exporting === 'excel'}>
              {exporting === 'excel' ? '⟳ Generating…' : '📊 Export Excel'}
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="rp-preview-head">
          <div className="rp-preview-title" style={{ '--c': activeType?.accent }}>
            <span className="rp-preview-dot" />
            Preview — {activeType?.label}
            <span className="rp-count-badge" style={{ '--c': activeType?.accent, '--bg-c': activeType?.bg }}>
              {filteredData.length} rows
            </span>
          </div>
          <div className="rp-footer-note">
            🖊 {getGeneratedByFooter()}
          </div>
        </div>

        {loading ? (
          <div className="rp-loading"><div className="rp-dot"/><div className="rp-dot"/><div className="rp-dot"/></div>
        ) : (
          <div className="rp-table-card">
            <div className="rp-data-list">
              {previewRows.length === 0 ? (
                <div className="rp-empty">
                  <span className="rp-empty-icon">{activeType?.icon}</span>
                  <div className="rp-empty-title">No data found</div>
                  <div className="rp-empty-sub">Try adjusting the filters above</div>
                </div>
              ) : (
                <>
                  {renderPreviewListHead()}
                  {paginatedRows.map((row, i) => renderPreviewListRow(row, i))}
                </>
              )}
            </div>

            {/* Mobile cards */}
            <div className="rp-cards" style={{ '--c': activeType?.accent }}>
              {previewRows.length === 0 ? (
                <div className="rp-empty">
                  <span className="rp-empty-icon">{activeType?.icon}</span>
                  <div className="rp-empty-title">No data found</div>
                  <div className="rp-empty-sub">Try adjusting the filters</div>
                </div>
              ) : (
                paginatedRows.map((row, i) => renderMobileCard(row, i))
              )}
            </div>

            {previewRows.length > 0 && (
              <div className="rp-pagination">
                <div className="rp-pagination-info">
                  Showing <strong>{rangeStart}–{rangeEnd}</strong> of <strong>{previewRows.length}</strong> rows
                </div>
                <div className="rp-pagination-controls">
                  <select
                    className="rp-pagination-size"
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    aria-label="Rows per page"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n} / page</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="rp-pagination-btn"
                    disabled={safePage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    ← Prev
                  </button>
                  <span className="rp-pagination-page">Page {safePage} of {totalPages}</span>
                  <button
                    type="button"
                    className="rp-pagination-btn"
                    disabled={safePage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Reports;
